/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedDate, useIntl } from 'react-intl'
import PaginationInfo from '../../../../client/js/components/pagination/PaginationInfo'
import PaginationNav from '../../../../client/js/components/pagination/PaginationNav'
import PaginationProvider from '../../../../client/js/components/pagination/PaginationProvider'
import SearchForm from '../../../../client/js/components/search/SearchForm'
import usePagination from '../../../../client/js/hooks/usePagination'
import { findManyTransactions } from '../../lib/rest/transactionAPIClient'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import { Box, Group, Modal, ModalProps, NavLink, SimpleGrid, Stack, Text } from '@mantine/core'
import Amount from './Amount'
import NoData from '../../../../client/js/components/NoData'

export type TransactionSelectionModalProps = ModalProps & {
  onClickResult (result: Transaction): void,
  queryFilter: Record<string, any>,
}

function TransactionSelectionModal (props: TransactionSelectionModalProps) {
  const {
    onClickResult,
    onClose,
    opened,
    queryFilter,
    ...others
  } = props

  const intl = useIntl()
  const [search, setSearch] = useState<string>(null)
  const [totalElements, setTotalElements] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [limit, setLimit] = useState<number>(10)
  const pagination = usePagination({
    totalElements,
    page,
    limit,
    onChange (page: number, limit: number) {
      setPage(page)
      setLimit(limit)
    }
  })

  const transactionsQuery = useQuery({
    enabled: opened,
    queryKey: ['transactions', queryFilter, search, page, limit],
    queryFn: () => findManyTransactions({
      ...queryFilter,
      search,
      page: pagination.page,
      limit: pagination.limit
    }).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })
  const items = useMemo(() => (
    (transactionsQuery.data != null ? getHalEmbedded(transactionsQuery.data)?.transactions : null) || []
  ), [transactionsQuery.data])

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, [setPage])

  useEffect(() => {
    if (!opened) {
      setSearch(null)
    }
  }, [opened])

  return (
    <Modal
      onClose={onClose}
      opened={opened}
      size="lg"
      title={intl.formatMessage({
        id: 'selection_of_transaction',
        defaultMessage: 'Sélection d\'une transaction'
      })}
      {...others}
    >
      <PaginationProvider context={pagination}>
        <Stack>
          <SearchForm
            onSubmit={handleSearchChange}
            submitting={transactionsQuery.isFetching}
          />
          <LoadingHelper
            query={transactionsQuery}
            render={() => (
              <Box>
                {items.length > 0
                  ? items.map((item) => (
                    <NavLink
                      key={item.id}
                      onClick={() => onClickResult(item)}
                      label={<Group>
                        <Text c="dimmed">
                          <FormattedDate value={item.dueAt} />
                        </Text>
                        <Text>{item.label}</Text>
                        <Amount colored ml="auto" value={item.amount + item.vat + item.cps} />
                      </Group>}
                    />
                  ))
                  : <NoData />
                }
              </Box>
            )}
          />
          <SimpleGrid cols={{ sm: 2 }}>
            <PaginationNav />
            <PaginationInfo ta="center" ml="auto" />
          </SimpleGrid>
        </Stack>
      </PaginationProvider>
    </Modal>
  )
}

export default TransactionSelectionModal
